import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, ChangeDetectionStrategy, inject, output, viewChild } from '@angular/core';
import { MealKitIconButtonComponent, MealKitTextInput } from '@yoni/mealcomponents-lib';
import { ModalService } from '../../../Services/modal.service';
import { DecimalPipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import {
  CourseItem,
  QdmService,
  RankItem,
  Restaurant,
  ServiceItem,
} from '../../../Services/qdm.service';
import { Subscription } from 'rxjs';
import { CartItem, CartService, CartStructure } from 'app/Cart/CartService';
import { PipeNumberFormat } from 'app/Components/Pipes/PipeNumberFormat';
import { AuthService, UserChildren } from '../../../Services/auth.service';
import {
  Enterprise,
  EnterpriseResponse,
  FullAdmissionsData,
  GestionService,
} from '../../../Services/gestion.service';
import {
  PaymentWallService,
  PaymentWay,
} from '../../../Services/payment-wall.service';
import { ToastrService } from 'ngx-toastr';
import { CookieService } from 'ngx-cookie-service';
import { ActionButtonComponent } from '../../action-button/action-button.component';
import { GreenActionButtonComponent } from '../../green-action-button/green-action-button.component';
import { IFrameGenerator } from '../../../Utils/IFrameGenerator';
import { Router } from '@angular/router';
import { OrderByPipe } from 'app/Pipes/OrderBy.pipe';
import { WeekMenuDisplayComponent } from '../../WeekMenuDisplay/WeekMenuDisplay.component';

@Component({
    selector: 'modal-cart-checkout',
    templateUrl: './ModalCartCheckout.component.html',
    styleUrl: './ModalCartCheckout.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
    MealKitIconButtonComponent,
    MealKitTextInput,
    TranslateModule,
    PipeNumberFormat,
    OrderByPipe,
    GreenActionButtonComponent
]
})
export class ModalCartCheckoutComponent implements OnDestroy, AfterViewInit {
  private variables = inject<any>('variables' as any);
  private modalService = inject(ModalService);
  private qdm = inject(QdmService);
  private cdr = inject(ChangeDetectorRef);
  protected cartService = inject(CartService);
  protected paymentWall = inject(PaymentWallService);
  private toast = inject(ToastrService);
  private auth = inject(AuthService);
  private gestion = inject(GestionService);
  private cookie = inject(CookieService);
  private router = inject(Router);

  readonly messageEvent = output<void>();
  readonly fadeInContainer = viewChild('fadeInContainer', { read: ElementRef });
  readonly summary = viewChild('summary', { read: ElementRef });
  readonly payments = viewChild('payments', { read: ElementRef });
  readonly orderComment = viewChild('orderComment', { read: MealKitTextInput });

  restaurant: Restaurant | null = null;
  cart: CartStructure | null = null;
  date: Date = new Date();
  child: UserChildren | null = null;
  service: ServiceItem | null = null;
  total: number = -1;
  admission: FullAdmissionsData | null = null;
  entData: EnterpriseResponse | null = null;
  wallet: number = 0;

  paymentsWays: PaymentWay[] = [];

  selectedPaymentsWays: PaymentWay[] = [];
  groupOrderQuantity: number = 1;

  iconsServices: { [key: number]: { url: string; bg: string } } = {};
  imagesWithErrors: Set<number> = new Set();
  
  private readonly ALT_BACKGROUND_COLORS = [
    '#F8B133', // orange-1
    '#8ABD27', // vert-1
    '#E57373', // rose-1
    '#64B5F6', // bleu-1
    '#FFB74D', // orange-3
    '#81C784', // vert-3
    '#F48FB1', // rose-3
  ];
  
  protected readOnlyMode: boolean = true;

  private sub: Subscription = new Subscription();
  protected enterprise: Enterprise | undefined = undefined;

  ngOnInit(): void {
    this.sub.add(
      this.qdm.selectedEnterprise$.subscribe((ent) => {
        this.enterprise = ent;
        this.cdr.detectChanges();
      })
    );
  }

  ngAfterViewInit(): void {
    this.date = this.variables.date;
    this.service = this.variables.service;
    this.child = this.variables.child;
    this.restaurant = this.variables.restaurant;
    this.total = this.variables.total;
    this.admission = this.variables.admission;
    this.entData = this.variables.enterprise;
    this.selectedPaymentsWays = [];
    this.groupOrderQuantity = this.variables.groupOrderQuantity;
    this.iconsServices = this.variables.iconsServices;

    this.sub.add(
      this.auth.user$.subscribe((user) => {
        this.wallet = user?.wallet.currency || 0;
        this.wallet /= 100;
      })
    );

    this.sub.add(
      this.qdm.selectedRestaurant$.subscribe((restaurant) => {
        if (restaurant) {
          this.restaurant = restaurant;
          this.cdr.detectChanges();
        }
      })
    );

    this.cartService.readCartContent();
    this.sub.add(
      this.cartService.cart$.subscribe((cart) => {
        this.cdr.detectChanges();
        if (!this.service) return;
        if (!this.restaurant) return;

        const serviceId = this.service.id;
        const restoId = this.restaurant.id;
        const childId = this.child ? this.child.id : -1;

        let filteredCart = cart.filter(
          (c) =>
            c.date === this.date.toLocaleDateString('fr-CA') &&
            c.childId === childId &&
            c.serviceId === serviceId &&
            c.restaurantId === restoId
        );
        if (filteredCart && filteredCart.length > 0) {
          this.cart = this.sortCart(filteredCart[0]);
        }
        this.cdr.detectChanges();
      })
    );
  }

  sortCart(cart: CartStructure): CartStructure {
    // Créer une copie profonde du panier pour éviter de modifier l'original
    let sortedCart = JSON.parse(JSON.stringify(cart));

    // Non extra item in top, extra at bottom
    sortedCart.items = sortedCart.items.sort((a: CartItem, b: CartItem) => {
      if (this.isExtra(a.id) && !this.isExtra(b.id)) return 1;
      if (!this.isExtra(a.id) && this.isExtra(b.id)) return -1;
      return 0;
    });
    sortedCart.items = sortedCart.items.sort((a: CartItem, b: CartItem) => {
      return b.rankId - a.rankId;
    });

    return sortedCart;
  }

  filteredCart(cart: CartStructure, extras: boolean): CartItem[] {
    if (!cart) return [];
    return cart.items.filter((item) => this.isExtra(item.id) === extras);
  }

  formatDate(date: Date | null): string {
    if (date === null) return '';

    // return "Lundi 12 Avril 2021";
    let fmt = date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });

    // uppercase first letter after space
    fmt = fmt.replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter.toUpperCase());

    // return fmt split by spaces, without first element
    return fmt.split(' ').slice(1).join(' ');
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  onCloseClick(): void {
    this.modalService.close();
  }

  removeFadeInFromContainer() {
    const fadeInContainer = this.fadeInContainer();
    if (!fadeInContainer) {
      return;
    }

    fadeInContainer.nativeElement.classList.remove('fadeIn');
  }

  generateImageUrl(restaurant: Restaurant): string {
    if (restaurant.logo === 'nologo.png') {
      return `${(globalThis as any).env.qdmApiUrl}/upload/logos/nologo.png`;
    }
    return `${(globalThis as any).env.qdmApiUrl}/upload/logos/${
      restaurant.id
    }/${restaurant.logo}`;
  }

  generateCourseImageUrl(image: string, id: number): string {
    if (!this.service) return '';
    const env = (globalThis as any).env;

    return `${env.qdmApiUrl}/upload/course_images/${this.service.rst_id}/image_${id}`;
  }
  
  getBackgroundColor(rankId: number): string {
    if (!this.service) return 'transparent';
    
    const icon = this.iconsServices[rankId];
    
    return (
      (icon?.bg ? `#${icon.bg}` : undefined) ||
      this.ALT_BACKGROUND_COLORS[
        this.service.id % this.ALT_BACKGROUND_COLORS.length
      ]
    );
  }
  
  onImageError(ev: ErrorEvent, item: CartItem) {
    if (!item) return;
    
    const target = ev.target as HTMLImageElement;
    this.imagesWithErrors.add(item.id);
    
    // Remplacer l'image
    this.replaceImageWithIcon(target, item);
  }
  
  private replaceImageWithIcon(targetElement: HTMLImageElement, item: CartItem) {
    if (!item) return;
    
    const rankId = item.rankId;
    const icon = this.iconsServices[rankId];
    
    // Obtenir le parent de l'image
    const containerDiv = targetElement.parentElement;
    if (!containerDiv) return;
    
    // Vider le conteneur
    containerDiv.innerHTML = '';
    
    if (!icon || !icon.bg || !icon.url) {
      // Fallback vers l'image par défaut si les icônes ne sont pas disponibles
      const imgElement = document.createElement('img');
      imgElement.src = WeekMenuDisplayComponent.IMAGE_NOT_FOUND_URL || '/assets/images/plats/default_image_menu.webp';
      imgElement.className = 'h-full aspect-square object-cover w-24';
      imgElement.alt = item.name;
      containerDiv.appendChild(imgElement);
      return;
    }
    
    // Créer un nouveau div avec l'image d'icône
    const iconDiv = document.createElement('div');
    iconDiv.style.backgroundColor = `#${icon.bg}`;
    iconDiv.className = 'h-full aspect-square object-cover w-24 relative overflow-hidden';
    iconDiv.innerHTML = `
      <img src="${icon.url}" class="w-12 object-cover absolute bottom-0 left-0 -translate-x-2 translate-y-2" alt="Icon" />
    `;
    
    containerDiv.appendChild(iconDiv);
    
    this.cdr.detectChanges();
  }

  close(ev: MouseEvent) {
    if (ev.target == ev.currentTarget) {
      this.modalService.close();
    }
  }

  isExtra(courseId: number): boolean {
    if(!this.enterprise?.isAcceptPayment) {
      return false;
    }
    
    if (!this.admission) return true;

    const rank = this.service?.components.find((c) =>
      c.courses.some((course) => course.id === courseId)
    );
    if (!rank) return false;

    const course = rank.courses.find((course) => course.id === courseId);
    if (!course) return false;

    return course.is_extra;
  }

  showPayementWall() {
    const inst = this as unknown as GreenActionButtonComponent;
    const data = inst.data() as ModalCartCheckoutComponent;

    if (data.enterprise && !data.enterprise?.isAcceptPayment) {
      data._handler_pay.bind(inst)();
      return;
    }

    if (data.entData?.canOrderMultiple) {
      data._handler_pay.bind(inst)();
      return;
    }

    inst._state.set('loading');
    inst._displayedLabel.set('Chargement des moyens de paiement...');

    if (!data.enterprise?.paymentOrder || data.enterprise.paymentOrder.length === 0) {
      data.toast.error(
        `Aucun moyen de paiement n'est disponible pour <b>${data.restaurant?.name}.</b><br>Veuillez ré-essayer plus tard, ou contacter le restaurateur.`,
        'Erreur de paiement',
        {
          positionClass: 'toast-bottom-center',
        }
      );
      return;
    }

    data.paymentsWays = data.enterprise.paymentOrder as PaymentWay[];
    
    if (data.paymentsWays.includes('WALLET')) {
      data.selectedPaymentsWays.push('WALLET');
    }
    data.cdr.detectChanges();
    const summary = data.summary();
    const payments = data.payments();
    if (summary && payments) {
      summary.nativeElement.classList.add('swipe-left');
      payments.nativeElement.classList.add('from-left');
    }
  }

  computeIconHtml(payment: PaymentWay): string {
    switch (payment) {
      case 'CB':
        return "<img src='/assets/images/logo/paygreen.webp' class='h-8 md:h-12' alt='Logo Multi-Paiement' />";
      case 'WALLET':
        return `<div class="pl-2 h-8 md:h-12 flex items-center"><span class="material-icons">wallet</span></div>`;
      case 'EDENRED':
        return "<img src='/assets/images/logo/edenred_logo.webp' class='h-8 md:h-12' alt='Logo Multi-Paiement' />";
    }
  }

  togglePaymentWay(payment: PaymentWay) {
    if (this.total > this.wallet && payment === 'WALLET') {
      return;
    }

    if (this.selectedPaymentsWays.includes(payment)) {
      if (this.selectedPaymentsWays.length > 1) {
        this.selectedPaymentsWays = this.selectedPaymentsWays.filter(
          (p) => p !== payment
        );
      }
    } else {
      if (this.selectedPaymentsWays.length < 2) {
        this.selectedPaymentsWays.push(payment);
      }
    }
  }

  isPaymentSelected(way: PaymentWay) {
    return this.selectedPaymentsWays.includes(way);
  }

  formatWay(way: PaymentWay): string {
    switch (way) {
      case 'CB':
        return 'Carte bancaire';
      case 'WALLET':
        return `Portefeuille (${new DecimalPipe('fr').transform(this.wallet, '1.2-2')}€)`;
      case 'EDENRED':
        return 'Ticket Restaurant';
    }
  }

  _handler_pay() {
    const inst: ActionButtonComponent =
      this as unknown as ActionButtonComponent;

    inst._state.set('loading');
    inst._displayedLabel.set('Génération du lien...');

    const modal = inst.data() as ModalCartCheckoutComponent;
    modal
      .sendOrder()
      .then((url) => {
        if (this.restaurant !== null) {
          modal.cookie.set(
            'lastOrderRestaurant',
            (modal.restaurant?.id || -1).toString(),
            1,
            '/'
          );
        }
        if (url === '') {
          modal.router.navigate(['/order/success']);
          modal.modalService.close();
          return;
        }

        let selectedPaymentsWays1 = modal.selectedPaymentsWays;
        if (selectedPaymentsWays1.includes('EDENRED')) {
          inst._state.set('success');
          inst._displayedLabel.set('Redirection en cours...');
          window.location.href = url;
        } else {
          IFrameGenerator.createAndAppendIframe(url);
          inst._state.set('success');
          inst._displayedLabel.set('Affichage du paiement...');
        }
      })
      .catch(() => {
        inst._state.set('failed');
        inst._displayedLabel.set('Erreur survenue...');

        setTimeout(() => {
          inst.reset();
        });
      });
  }

  sendOrder(): Promise<string> {
    return new Promise((resolve, reject) => {
      this.gestion
        .fetchCompanyId(this.restaurant?.id || -1, this.restaurant?.cstID || -1)
        .then((companyId) => {
          const ways = this.selectedPaymentsWays;
          const deliveryDate = this.date.formatDate('forApi');
          const price = this.total;
          const userEnterpriseData = this.entData?.id || -1;
          const isPaid = false;
          const numberOfAdmission = this.groupOrderQuantity;
          let remainingAmount = price;

          let paymentWays = ways
            .map((way) => {
              let amount = 0;

              switch (way) {
                case 'WALLET':
                  amount = Math.min(this.wallet, remainingAmount);
                  remainingAmount -= amount;
                  break;
                case 'CB':
                  if (remainingAmount > 0) {
                    amount = remainingAmount; // Prendre le reste du montant
                    remainingAmount = 0; // Le reste est maintenant payé
                  }
                  break;
                case 'EDENRED':
                  if (remainingAmount > 0) {
                    amount = remainingAmount; // Ajustez selon la logique spécifique si nécessaire
                    remainingAmount = 0; // Le reste est maintenant payé
                  }
                  break;
                default:
                  amount = 0;
              }

              return { [way]: amount.toFixed(2) };
            })
            .filter((payment) => parseFloat(Object.values(payment)[0]) > 0); // Enlever les paiements à 0
          const isOrderForGroup = this.entData?.canOrderMultiple || false;
          const children = this.child?.id || null;

          const orderComment = this.orderComment();
          let templateBody = {
            orderItems: this.cart?.items
              .map((item) => {
                const i = this.getItemById(item.id);
                if (!i) return null;

                const rank = this.getRankForItem(item.id);

                if (!rank) return null;

                return {
                  platId: item.id,
                  serviceContent: item.name,
                  vat: i.vat ?? 5.5,
                  price: i.price ?? 0,
                  quantity: item.quantity,
                  serviceId: rank.family_id,
                  componentId: rank.id,
                  isExtra: this.isExtra(item.id),
                  gpaoId: item.gpaoId,
                  rankSort: parseInt(rank.rank || '0'),
                };
              })
              .filter((item) => item !== null)
              .sort(
                (a, b) =>
                  parseInt(a!.rankSort.toString()) -
                  parseInt(b!.rankSort.toString())
              ),
            price: price,
            userEnterpriseData: userEnterpriseData,
            enterprise: this.enterprise?.id,
            company: companyId,
            isPaid: isPaid,
            deliveryDate: deliveryDate,
            numberOfAdmission: numberOfAdmission,
            paymentWays: paymentWays,
            isOrderForGroup: isOrderForGroup,
            children: children,
            admissionId: this.admission?.admissionId || null,
            admissionPrice: this.admission?.admissionPrice || null,
            svc_id: this.service?.svc_id || -1,
            professionalSocialCategory:
              this.entData?.professionalSocialCategory,
            mealtime_qdm_id: this.service?.mealtime_id,
            comment: orderComment?.value.trim()
              ? orderComment.value.trim()
              : null,
          };

          this.gestion.pushOrder(templateBody).then((res: any) => {
            if (res.success) {
              if (res.url !== '') {
                resolve(res.url);
              } else {
                resolve('');
              }
            } else {
              reject();
            }
          });
        });
    });
  }

  getItemById(id: number): CourseItem | undefined {
    let it: CourseItem | undefined;

    this.service?.components.forEach((serv) => {
      const i = serv.courses.find((c) => c.id === id);

      if (i) {
        it = i;
      }
    });

    return it;
  }

  getRankForItem(courseId: number): RankItem | undefined {
    if (!this.service) return undefined;
    return this.service.components.find((c) =>
      c.courses.some((course) => course.id === courseId)
    );
  }
}
